import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft, Briefcase } from 'lucide-react';

export default function ProviderTermsPage() {
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'Provider Terms',
      lastUpdate: 'Last updated: March 2026',
      intro: 'These terms apply specifically to experience providers (agencies) using BidTrip to offer their services.',
      sections: [
        {
          title: '1. Provider Registration',
          content: `1.1. To register as a Provider on BidTrip, you must:
• Be a legally established business in Mexico or have valid permits to operate
• Provide accurate business information (company name, contact details)
• Select the categories of experiences you offer (yachts, tours, activities, etc.)
• Specify the locations where you operate

1.2. BidTrip reserves the right to verify your business information and may request supporting documentation.

1.3. Providing false or misleading information may result in account suspension or termination.`
        },
        {
          title: '2. Service Standards',
          content: `As a Provider, you agree to:
• Deliver experiences as described in your offers
• Maintain professional conduct with all clients
• Ensure all necessary permits and licenses are current
• Provide adequate safety equipment and briefings
• Have appropriate insurance coverage for your activities
• Respond to booking requests in a timely manner
• Honor confirmed bookings except in cases of force majeure`
        },
        {
          title: '3. Offers and Pricing',
          content: `3.1. You may submit offers to User requests that match your service categories and locations.

3.2. Your offers must include:
• Clear description of the experience
• Total price in USD
• What is included (equipment, food, drinks, etc.)
• Duration of the experience
• Up to 3 photos of your service

3.3. Prices must be final and include all applicable taxes and fees.

3.4. You may not contact Users outside the platform to circumvent BidTrip's commission system.`
        },
        {
          title: '4. Commission and Fees',
          content: `4.1. BidTrip charges a 15% commission on each completed booking (or 10% if the User registered with a referral code, with 5% going to the referring Partner).

4.2. You receive 85% of the booking total.

4.3. Commission is calculated on the total booking amount paid by the User.

4.4. Example breakdown for a $1,000 USD booking:
• Without referral: Provider receives $850, BidTrip receives $150
• With referral: Provider receives $850, BidTrip receives $100, Partner receives $50`
        },
        {
          title: '5. Payment and Withdrawals',
          content: `5.1. Escrow System:
• User payments are held in escrow until the experience is completed
• Funds are released to your available balance only after the booking is marked as "Completed"
• Either you or the User can mark the experience as completed

5.2. Withdrawals:
• You can withdraw available funds instantly to your registered bank account
• Bank account information required: Account holder name, Bank name, CLABE (18 digits)
• Withdrawals are processed immediately through our payment partner
• Minimum withdrawal amount: $100 USD

5.3. Payment Schedule:
• Funds become available within 24 hours after experience completion
• No approval needed for withdrawals`
        },
        {
          title: '6. Cancellation Policy',
          content: `6.1. Provider-Initiated Cancellations:
• You should only cancel bookings in exceptional circumstances
• Excessive cancellations may result in account penalties or suspension
• You must notify the User as soon as possible through the platform

6.2. User-Initiated Cancellations:
• If a User cancels a paid booking, you receive 10% of the booking total as compensation
• The remaining cancellation fee (10%) goes to BidTrip

6.3. No-Shows:
• If a User fails to appear, you may mark the booking as "No-Show"
• No-show bookings are treated as completed for payment purposes`
        },
        {
          title: '7. Ratings and Reviews',
          content: `7.1. Users can rate and review your services after each completed experience.

7.2. Ratings are displayed on your public profile and affect your visibility on the platform.

7.3. You may not:
• Offer incentives for positive reviews
• Threaten or coerce Users regarding reviews
• Create fake reviews

7.4. You may respond professionally to reviews through the platform.`
        },
        {
          title: '8. Profile and Content',
          content: `8.1. Your Provider profile includes:
• Company name and logo
• Description of your business
• Service categories and locations
• Social media links (optional)
• Average rating and reviews

8.2. Content Guidelines:
• All photos must be of your actual services
• Descriptions must be accurate and not misleading
• Content must not violate any laws or third-party rights
• BidTrip may remove inappropriate content`
        },
        {
          title: '9. Insurance and Liability',
          content: `9.1. You are required to maintain adequate insurance coverage for:
• Third-party liability
• Participant injury
• Property damage
• Vehicle/vessel coverage (if applicable)

9.2. You agree to indemnify BidTrip against any claims arising from:
• Negligence in service delivery
• Accidents or injuries during experiences
• Property damage
• Breach of these terms

9.3. BidTrip is not liable for any incidents during your experiences.`
        },
        {
          title: '10. Verification and Compliance',
          content: `10.1. BidTrip may request verification documents including:
• Business registration/incorporation documents
• Operating permits and licenses
• Insurance certificates
• Vehicle/vessel registration (if applicable)
• Staff certifications (diving, navigation, etc.)

10.2. Verified Providers receive a verification badge on their profile.

10.3. Operating without required permits or licenses is grounds for immediate account termination.`
        },
        {
          title: '11. Communication',
          content: `11.1. All communication with Users regarding bookings must occur through the BidTrip platform.

11.2. After a booking is confirmed and paid, you can use the in-app chat to coordinate details.

11.3. You may not:
• Share personal contact information to bypass the platform
• Solicit Users for direct bookings
• Send spam or promotional messages`
        },
        {
          title: '12. Account Suspension and Termination',
          content: `BidTrip may suspend or terminate your account for:
• Violation of these Provider Terms
• Poor service quality (consistently low ratings)
• Excessive cancellations
• Fraudulent activity
• Circumventing platform fees
• Harassment of Users or staff
• Operating without required permits

You may appeal suspension decisions by contacting legal@bidtrip.live.`
        },
        {
          title: '13. Modifications',
          content: `BidTrip reserves the right to modify these Provider Terms. We will notify you of significant changes at least 30 days in advance via email. Continued use of the platform after changes take effect constitutes acceptance of the new terms.`
        },
        {
          title: '14. Contact',
          content: `For questions about these Provider Terms:
• Email: legal@bidtrip.live
• Provider Support: soporte@bidtrip.live

For urgent booking issues, use the in-app support chat for faster response.`
        }
      ]
    },
    es: {
      title: 'Términos para Proveedores',
      lastUpdate: 'Última actualización: Marzo 2026',
      intro: 'Estos términos aplican específicamente a los proveedores de experiencias (agencias) que usan BidTrip para ofrecer sus servicios.',
      sections: [
        {
          title: '1. Registro de Proveedor',
          content: `1.1. Para registrarte como Proveedor en BidTrip, debes:
• Ser un negocio legalmente establecido en México o tener permisos válidos para operar
• Proporcionar información precisa del negocio (nombre de la empresa, datos de contacto)
• Seleccionar las categorías de experiencias que ofreces (yates, tours, actividades, etc.)
• Especificar las ubicaciones donde operas

1.2. BidTrip se reserva el derecho de verificar tu información comercial y puede solicitar documentación de respaldo.

1.3. Proporcionar información falsa o engañosa puede resultar en suspensión o terminación de la cuenta.`
        },
        {
          title: '2. Estándares de Servicio',
          content: `Como Proveedor, aceptas:
• Entregar experiencias como se describen en tus ofertas
• Mantener una conducta profesional con todos los clientes
• Asegurar que todos los permisos y licencias necesarios estén vigentes
• Proporcionar equipo de seguridad adecuado e instrucciones
• Tener cobertura de seguro apropiada para tus actividades
• Responder a las solicitudes de reservación de manera oportuna
• Honrar las reservaciones confirmadas excepto en casos de fuerza mayor`
        },
        {
          title: '3. Ofertas y Precios',
          content: `3.1. Puedes enviar ofertas a las solicitudes de Usuarios que coincidan con tus categorías de servicio y ubicaciones.

3.2. Tus ofertas deben incluir:
• Descripción clara de la experiencia
• Precio total en USD
• Qué está incluido (equipo, comida, bebidas, etc.)
• Duración de la experiencia
• Hasta 3 fotos de tu servicio

3.3. Los precios deben ser finales e incluir todos los impuestos y tarifas aplicables.

3.4. No puedes contactar a Usuarios fuera de la plataforma para evadir el sistema de comisiones de BidTrip.`
        },
        {
          title: '4. Comisión y Tarifas',
          content: `4.1. BidTrip cobra una comisión del 15% en cada reservación completada (o 10% si el Usuario se registró con un código de referido, con 5% yendo al Partner que refirió).

4.2. Tú recibes el 85% del total de la reservación.

4.3. La comisión se calcula sobre el monto total de la reservación pagado por el Usuario.

4.4. Ejemplo de desglose para una reservación de $1,000 USD:
• Sin referido: Proveedor recibe $850, BidTrip recibe $150
• Con referido: Proveedor recibe $850, BidTrip recibe $100, Partner recibe $50`
        },
        {
          title: '5. Pagos y Retiros',
          content: `5.1. Sistema de Escrow:
• Los pagos de Usuarios se mantienen en escrow hasta que la experiencia se complete
• Los fondos se liberan a tu saldo disponible solo después de que la reservación se marque como "Completada"
• Tú o el Usuario pueden marcar la experiencia como completada

5.2. Retiros:
• Puedes retirar fondos disponibles instantáneamente a tu cuenta bancaria registrada
• Información bancaria requerida: Nombre del titular, Nombre del banco, CLABE (18 dígitos)
• Los retiros se procesan inmediatamente a través de nuestro socio de pagos
• Monto mínimo de retiro: $100 USD

5.3. Calendario de Pagos:
• Los fondos están disponibles dentro de 24 horas después de completar la experiencia
• No se necesita aprobación para retiros`
        },
        {
          title: '6. Política de Cancelación',
          content: `6.1. Cancelaciones Iniciadas por el Proveedor:
• Solo debes cancelar reservaciones en circunstancias excepcionales
• Las cancelaciones excesivas pueden resultar en penalizaciones o suspensión de la cuenta
• Debes notificar al Usuario lo antes posible a través de la plataforma

6.2. Cancelaciones Iniciadas por el Usuario:
• Si un Usuario cancela una reservación pagada, recibes el 10% del total de la reservación como compensación
• La tarifa de cancelación restante (10%) va a BidTrip

6.3. No-Shows:
• Si un Usuario no se presenta, puedes marcar la reservación como "No-Show"
• Las reservaciones no-show se tratan como completadas para propósitos de pago`
        },
        {
          title: '7. Calificaciones y Reseñas',
          content: `7.1. Los Usuarios pueden calificar y reseñar tus servicios después de cada experiencia completada.

7.2. Las calificaciones se muestran en tu perfil público y afectan tu visibilidad en la plataforma.

7.3. No puedes:
• Ofrecer incentivos por reseñas positivas
• Amenazar o coaccionar a Usuarios sobre reseñas
• Crear reseñas falsas

7.4. Puedes responder profesionalmente a las reseñas a través de la plataforma.`
        },
        {
          title: '8. Perfil y Contenido',
          content: `8.1. Tu perfil de Proveedor incluye:
• Nombre de la empresa y logo
• Descripción de tu negocio
• Categorías de servicio y ubicaciones
• Enlaces a redes sociales (opcional)
• Calificación promedio y reseñas

8.2. Lineamientos de Contenido:
• Todas las fotos deben ser de tus servicios reales
• Las descripciones deben ser precisas y no engañosas
• El contenido no debe violar ninguna ley o derechos de terceros
• BidTrip puede eliminar contenido inapropiado`
        },
        {
          title: '9. Seguros y Responsabilidad',
          content: `9.1. Debes mantener cobertura de seguro adecuada para:
• Responsabilidad civil
• Lesiones de participantes
• Daños a la propiedad
• Cobertura de vehículo/embarcación (si aplica)

9.2. Aceptas indemnizar a BidTrip contra cualquier reclamo que surja de:
• Negligencia en la entrega del servicio
• Accidentes o lesiones durante las experiencias
• Daños a la propiedad
• Incumplimiento de estos términos

9.3. BidTrip no es responsable de ningún incidente durante tus experiencias.`
        },
        {
          title: '10. Verificación y Cumplimiento',
          content: `10.1. BidTrip puede solicitar documentos de verificación incluyendo:
• Documentos de registro/incorporación del negocio
• Permisos y licencias de operación
• Certificados de seguro
• Registro de vehículo/embarcación (si aplica)
• Certificaciones del personal (buceo, navegación, etc.)

10.2. Los Proveedores verificados reciben una insignia de verificación en su perfil.

10.3. Operar sin los permisos o licencias requeridos es motivo de terminación inmediata de la cuenta.`
        },
        {
          title: '11. Comunicación',
          content: `11.1. Toda comunicación con Usuarios sobre reservaciones debe ocurrir a través de la plataforma BidTrip.

11.2. Después de que una reservación esté confirmada y pagada, puedes usar el chat en la app para coordinar detalles.

11.3. No puedes:
• Compartir información de contacto personal para evadir la plataforma
• Solicitar a Usuarios reservaciones directas
• Enviar spam o mensajes promocionales`
        },
        {
          title: '12. Suspensión y Terminación de Cuenta',
          content: `BidTrip puede suspender o terminar tu cuenta por:
• Violación de estos Términos para Proveedores
• Mala calidad de servicio (calificaciones consistentemente bajas)
• Cancelaciones excesivas
• Actividad fraudulenta
• Evadir las tarifas de la plataforma
• Acoso a Usuarios o personal
• Operar sin los permisos requeridos

Puedes apelar decisiones de suspensión contactando a legal@bidtrip.live.`
        },
        {
          title: '13. Modificaciones',
          content: `BidTrip se reserva el derecho de modificar estos Términos para Proveedores. Te notificaremos de cambios significativos con al menos 30 días de anticipación por correo electrónico. El uso continuado de la plataforma después de que los cambios tomen efecto constituye aceptación de los nuevos términos.`
        },
        {
          title: '14. Contacto',
          content: `Para preguntas sobre estos Términos para Proveedores:
• Correo: legal@bidtrip.live
• Soporte para Proveedores: soporte@bidtrip.live

Para problemas urgentes con reservaciones, usa el chat de soporte en la app para una respuesta más rápida.`
        }
      ]
    }
  };

  const t = content[language] || content.es;

  return (
    <div className="min-h-screen bg-muted/30" data-testid="provider-terms-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft size={24} />
          </Link>
          <div className="flex items-center gap-2">
            <Briefcase className="text-caribbean" size={24} />
            <h1 className="font-heading text-xl font-bold">{t.title}</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm p-6 md:p-10">
          {/* Logo */}
          <div className="text-center mb-8">
            <h2 className="font-heading text-3xl font-bold">
              Bid<span className="text-caribbean">Trip</span>
            </h2>
            <p className="text-sm text-muted-foreground mt-2">{t.lastUpdate}</p>
          </div>

          {/* Intro */}
          <div className="bg-caribbean/10 border border-caribbean/20 rounded-xl p-4 mb-8">
            <p className="text-sm text-caribbean font-medium">{t.intro}</p>
          </div>

          {/* Sections */}
          <div className="space-y-8">
            {t.sections.map((section, index) => (
              <section key={index} className="prose prose-slate max-w-none">
                <h3 className="font-heading text-lg font-semibold text-foreground mb-3">
                  {section.title}
                </h3>
                <div className="text-muted-foreground whitespace-pre-line text-sm leading-relaxed">
                  {section.content}
                </div>
              </section>
            ))}
          </div>

          {/* Footer Links */}
          <div className="mt-12 pt-8 border-t flex flex-wrap gap-4 justify-center text-sm">
            <Link to="/terms" className="text-caribbean hover:underline">
              {language === 'en' ? 'Terms and Conditions' : 'Términos y Condiciones'}
            </Link>
            <span className="text-muted-foreground">•</span>
            <Link to="/privacy" className="text-caribbean hover:underline">
              {language === 'en' ? 'Privacy Policy' : 'Política de Privacidad'}
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
